import React, { useEffect, useRef, useState } from "react";
import LaddaButton from "../buttons/laddaButton";
import { IconButton, Tooltip, useMediaQuery } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const SimpleSignComponent = ({
  setSignature,
  signature,
  hideButtons = false,
  hideSave = false,
  onSave = async () => {},
  disabled = false,
  captureOnStrokeEnd = false,
  clearWithIcon = false
}) => {
  const isSmallScreen = useMediaQuery("(max-width:590px)");
  const canvasRef = useRef(null);
  const strokeChangedRef = useRef(false);
  const capturedSignatureRef = useRef(null);
  const canvasSizeRef = useRef("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvasSize = `${canvasRef.current.width}:${canvasRef.current.height}`;
    if (captureOnStrokeEnd && signature === capturedSignatureRef.current &&
        canvasSize === canvasSizeRef.current) return;
    canvasSizeRef.current = canvasSize;

    const context = canvasRef.current.getContext("2d");
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    if (signature) {
      const img = new Image();
      img.src = signature;
      img.onload = () => {
        context.drawImage(
          img,
          0,
          0,
          canvasRef.current.width,
          canvasRef.current.height
        );
      };
      img.onerror = () =>
        console.error("Error al cargar la imagen de la firma.");
      return () => { img.onload = null; };
    }
  }, [signature, captureOnStrokeEnd, isSmallScreen]);

  const handleClear = () => {
    const context = canvasRef.current.getContext("2d");
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setSignature(null);
    capturedSignatureRef.current = null;
    strokeChangedRef.current = false;
  };

  const handleSaveSignature = async () => {
    const dataUrl = canvasRef.current.toDataURL("image/png");
    setSignature(dataUrl);

    try {
      setLoading(true);
      await onSave(dataUrl);
      console.log("Firma guardada correctamente.");
    } catch (error) {
      console.error("❌ Error al guardar la firma con onSave:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  // Eventos de mouse
  const startDrawing = (e) => {
    if (disabled) return;
    const context = canvasRef.current.getContext("2d");
    const { x, y } = getCanvasCoords(e.nativeEvent);
    context.beginPath();
    context.moveTo(x, y);
    strokeChangedRef.current = false;
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    const context = canvasRef.current.getContext("2d");
    const { x, y } = getCanvasCoords(e.nativeEvent);
    context.lineTo(x, y);
    context.stroke();
    strokeChangedRef.current = true;
  };

  const stopDrawing = () => {
    if (disabled) return;
    setIsDrawing(false);
    if (captureOnStrokeEnd && strokeChangedRef.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      capturedSignatureRef.current = dataUrl;
      strokeChangedRef.current = false;
      setSignature(dataUrl);
    }
  };

  const getTouchCanvasCoords = (touch) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY
    };
  };

  // Eventos táctiles
  const startTouchDrawing = (e) => {
    if (disabled) return;
    const touch = e.touches[0];
    const context = canvasRef.current.getContext("2d");
    const { x, y } = getTouchCanvasCoords(touch);
    context.beginPath();
    context.moveTo(x, y);
    strokeChangedRef.current = false;
    setIsDrawing(true);
  };

  const touchDraw = (e) => {
    if (!isDrawing || disabled) return;
    const touch = e.touches[0];
    const context = canvasRef.current.getContext("2d");
    const { x, y } = getTouchCanvasCoords(touch);
    context.lineTo(x, y);
    context.stroke();
    strokeChangedRef.current = true;
  };

  return (
    <div className="column-center" style={{ width: "100%" }}>
      <div style={{ width: "100%", padding: "10px" }}>
        <canvas
          ref={canvasRef}
          width={isSmallScreen ? 300 : 450}
          height={300}
          style={{
            width: "100%",
            aspectRatio: "3 / 2",
            display: "block",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: disabled ? "default" : "crosshair",
            backgroundColor: disabled ? "#f5f5f5" : "white",
            touchAction: "none" // Previene el scroll mientras se dibuja
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startTouchDrawing}
          onTouchMove={touchDraw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />
      </div>

      {!hideButtons && !disabled && (
        <div
          className="container-inputs"
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            paddingBottom: "30px"
          }}
        >
          {clearWithIcon ? (
            <Tooltip title="Limpiar firma">
              <IconButton aria-label="Limpiar firma" onClick={handleClear}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          ) : <LaddaButton
            fontSize="12px"
            height="30px"
            variant="contained"
            onClick={handleClear}
          >
            Limpiar
          </LaddaButton>}
          {!hideSave && (
            <LaddaButton
              width="160px"
              fontSize="12px"
              height="40px"
              variant="contained"
              onClick={handleSaveSignature}
              disabled={loading}
            >
              Guardar
            </LaddaButton>
          )}
        </div>
      )}
    </div>
  );
};

export default SimpleSignComponent;
